import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { translateMessages, isPresent, getRelativePath } from '@/lib/utils';

const baseRInputProps = {
  actionParameter: {
    type: Object,
    default: null,
  },
  chips: {
    type: Boolean,
    default: false,
  },
  classMeta: {
    type: Object,
    default: null,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  comboIdKeys: {
    type: Array,
    default: null,
  },
  // when used update-value emits up _id for the value
  compositeKeyNeeded: {
    type: Boolean,
    default: false,
  },
  customRemoveFn: {
    type: Function,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  emitObject: {
    type: Boolean,
    default: false,
  },
  emitValueOnly: {
    type: Boolean,
    default: false,
  },
  enabledOverride: {
    type: Boolean,
    // NOTE: default is null so we can tell if explicitly set
    default: null,
  },
  errors: {
    type: Array,
    required: false,
  },
  falseValue: {
    type: [Boolean, String],
    default: 'N',
  },
  fieldAuthInstance: {
    type: Boolean,
    default: false,
  },
  fieldAuthProps: {
    type: Object,
    default: null,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  hideDetails: {
    type: Boolean,
    default: false,
  },
  hint: {
    type: String,
    default: '',
  },
  inputTypeOverride: {
    type: String,
    default: null,
  },
  isDataTableField: {
    type: Boolean,
    default: false,
  },
  isFraction: {
    type: Boolean,
    default: false,
  },
  isPersisted: {
    type: Boolean,
    required: false,
  },
  isSearchField: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: null,
  },
  messagePropName: {
    type: String,
    default: null,
  },
  meta: {
    type: Object,
    default: () => {},
  },
  model: {
    type: Object,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  parentPath: {
    required: false,
  },
  persistentHint: {
    type: Boolean,
    default: false,
  },
  prepend: {
    type: Boolean,
    default: false,
  },
  rComponentName: {
    type: String,
    default: null,
  },
  reRenderKey: {
    type: String,
    default: null,
  },
  requiredFields: {
    type: Array,
    default: null,
  },
  restrictableName: {
    type: String,
    default: null,
  },
  singleLine: {
    type: Boolean,
    default: false,
  },
  trueValue: {
    type: [Boolean, String],
    default: 'Y',
  },
  useMinWidth: {
    type: Boolean,
    default: true,
  },
  value: {
    required: false,
  },
  warnings: {
    type: Array,
    required: false,
  },
  width: {
    type: Number,
    default: 0,
  },
};

const baseRInputEmits = [
  'clear-messages',
  'has-messages',
  'update-value',
  'update-field-auth',
  'toggle-field-auth',
  'blur',
  'focus',
  'tab',
  'enter',
];

// eslint-disable-next-line max-lines-per-function
const useBaseRInput = (props, emit, refs) => {
  const store = useStore();

  const autoUpdateInFlight = computed(() => store.getters['editObject/autoUpdateInFlight']);
  const showContextMenu = computed(() => store.getters['editObject/showContextMenu']);

  const {
    classSchema,
    propertyMeta,
    localLabel,
    masked,
    maskedRE,
  } = useMeta();

  const {
    localErrors,
    errorsAndWarningMessages,
    hasErrorMessages,
    hasWarnings,
  } = useErrorsAndWarnings();

  const {
    hasLockIcon,
    isAllowed,
    isPercent,
    isCurrency,
    isFieldAuth,
    isReadOnly,
    isMissingEditPermission,
    isDisabled,
    isRequired,
  } = useInputMetaChecks();

  const {
    isRestricted,
    disableInput,
    displayRestrictedAttributeLabel,
  } = useRestricted();

  const {
    dialogActivator,
    mergedFieldAuthProps,
    updateFieldAuth,
    toggleFieldAuth,
  } = useFieldAuth();

  const {
    tooltipText,
    showToolTip,
    tooltipHandler,
  } = useTooltips();

  const {
    updateValue,
    localValue,
    emitFieldEvent,
    handleEnterKey,
    handleSearchSubmit,
    setDefaultValue,
  } = useManageValue();

  onMounted(async () => {
    setDefaultValue();
    const el = refs?.[props.name]?.value?.$el;
    if (!el) return;

    if (props.fieldAuthInstance) return;
    const messageElement = el.querySelector('.v-messages');
    if (messageElement) {
      messageElement.addEventListener('click', event => {
        const count = errorsAndWarningMessages.value.length;
        if (!count || showContextMenu.value) return;

        const messagePayload = {
          scope: 'field',
          fields: [props.messagePropName || props.name],
          path: props.parentPath,
        };

        const menuItems = [
          {
            label: 'Dismiss message(s)',
            closure: () => props.customRemoveFn() || store.dispatch('editObject/removeMessages', messagePayload),
          },
        ];

        store.dispatch('editObject/toggleContextMenu', { event, menuItems });
      });
    }

    if (!props.clearable) return;
    el.querySelector('.v-field__clearable > i')?.setAttribute('tabindex', '-1');
  });


  function useMeta() {
    const classSchema = computed(() => {
      return props.classMeta || store.getters['meta/class_schema'](props.model._classname);
    });

    const propertyMeta = computed(() => _get(props.meta, 'property_meta', null));

    const localLabel = computed(() => {
      // Fallbacks: passed prop label, meta data labels.label, translation label, meta data labels, followed by uppercasing name prop.
      if (props.label === '') return props.label;
      return props.label ||
              _get(props.meta, 'labels.label') ||
              store.getters['meta/property_label'](props.model._classname, props.name) ||
              (typeof _get(props.meta, 'labels') === 'string' ? _get(props.meta, 'labels') : '') ||
              props.name.charAt(0).toUpperCase() + props.name.slice(1);
    });

    const masked = ref(0);
    const maskedRE = computed(() => {
      return new RegExp(`.(?=.{${masked.value.toString()}})`, 'g');
    });

    return {
      classSchema,
      propertyMeta,
      localLabel,
      masked,
      maskedRE,
    };
  }

  function useErrorsAndWarnings() {
    /**
     * Serves as a means to set passed errors and warnings from parent. This method
     * will be called on created and update hooks. It is the parent's responsibility
     * to clear out any passed errors and warnings.
     * @param {Array} errors - Represents a collection of error messages.
     * @param {Array} warnings - Represents a collection of warning messages.
     */
    const setErrorsAndWarnings = ({ errors, warnings }) => {
      if (errors) errorMessages.value = errors;
      if (warnings) warningMessages.value = warnings;
    };

    const localErrors = ref([]);
    const localWarnings = ref([]);
    const collectMessages = (key) => {
      let messages = [];
      const _problemNames = [..._get(propertyMeta.value, '_problemNames', [])];

      const propName = props.messagePropName || props.name;
      if (propName) _problemNames.push(propName);
      for (const name of _problemNames) {
        const m = props.model[`_${name}_${key}`];

        if (m) messages = [...messages, ...translateMessages(m, store)];
      }
      return messages;
    };

    const errorMessages = computed({
      get() {
        return [...collectMessages('errors'), ...localErrors.value];
      },
      set(newVal) {
        localErrors.value = newVal;
      },
    });

    const warningMessages = computed({
      get() {
        return [...collectMessages('warnings'), ...localWarnings.value];
      },
      set(newVal) {
        localWarnings.value = newVal;
      },
    });

    const errorsAndWarningMessages = computed(() => {
      return [...errorMessages.value, ...warningMessages.value];
    });

    // This only exists for class binding so that Vuetify can render disabled fields in error.
    const hasErrorMessages = computed(() => {
      return errorMessages.value.length > 0;
    });

    // This only exists for class binding as Vuetify has no concept of warnings.
    const hasWarnings = computed(() => {
      return warningMessages.value.length > 0 && !hasErrorMessages.value;
    });

    watch(() => props.errors,
      (newVal, oldVal) => {
        if (newVal && newVal !== oldVal) {
          setErrorsAndWarnings({ errors: newVal });
        }
      },
      { deep: true, immediate: true });

    watch(() => props.warnings,
      (newVal, oldVal) => {
        if (newVal && newVal !== oldVal) {
          setErrorsAndWarnings({ warnings: newVal });
        }
      },
      { deep: true, immediate: true });

    watch(errorsAndWarningMessages,
      (newVal, oldVal) => {
        const payload = { errors: errorMessages.value, warnings: warningMessages.value, key: props.name };

        if (newVal.length === 0 && oldVal.length > 0) emit('clear-messages', payload);
        if (newVal.length > 0) emit('has-messages', payload);
      });

    return {
      localErrors,
      errorsAndWarningMessages,
      hasErrorMessages,
      hasWarnings,
    };
  }

  function useInputMetaChecks() {
    const isAllowed = computed(() => {
      // NOTE: we also recalculate 'this.masked' here
      masked.value = 0;
      const pm = propertyMeta.value;
      const perm = _get(pm, 'permission');
      if (perm) {
        const hasPerm = store.getters['permissions/isPermPresent'](perm);
        if (!hasPerm) {
          const unmasked = parseInt(_get(pm, 'unmaskeddigits'), 10);
          if (unmasked) {
            // masked acts as both a Boolean and how many chars to unmask:
            masked.value = unmasked;
            if (['RAutocomplete', 'RCombobox'].includes(props.rComponentName)) {
              // NOTE: currently we only support user input for these types of
              // masked fields when in an RAutocomplete.  Otherwise, it remains locked
              // as a password
              return true;
            }
          }
        }
        return hasPerm;
      }
      return true;
    });

    // Move to RTextField
    const isPercent = computed(() => _get(propertyMeta.value, 'datatype') === 'percent');
    const isCurrency = computed(() => _get(propertyMeta.value, 'datatype') === 'currency');
    const isFieldAuthOverride = computed(() => !!_get(propertyMeta.value, 'field_auth_override'));

    const isFieldAuth = computed(() => {
      return (_get(classSchema.value, `fieldauths.${props.name}`) ||
        isFieldAuthOverride.value) &&
        !isRestricted.value;
    });

    const hasLockIcon = computed(() => isFieldAuth.value && !props.fieldAuthInstance);

    const isReadOnly = computed(() => {
      if (props.enabledOverride) return false;
      const forceEnabled = _get(props.meta, 'property_meta._enabled', false);
      if (props.fieldAuthInstance || forceEnabled) return false;

      const isReadOnlyPersistent = propertyMeta.value && propertyMeta.value.readonly === 'persistent' && props.isPersisted;
      const readOnly = propertyMeta.value && (propertyMeta.value.readonly === true || isReadOnlyPersistent);
      const isPrimaryKey = propertyMeta.value && propertyMeta.value.primary_key === true;
      return (isFieldAuth.value && !isFieldAuthOverride.value) ||
        readOnly ||
        !isAllowed.value ||
        (isPrimaryKey && props.isPersisted);
    });

    const isMissingEditPermission = computed(() => {
      return _get(props.classMeta, 'table_meta.explicit_permissions') &&
        store.getters['permissions/screenPermissions'].editPermission === false;
    });

    const isDisabled = computed(() => {
      // NOTE: enabledOverride defaults to null:
      if (isPresent(props.enabledOverride)) return !props.enabledOverride;
      if (props.disabled) return props.disabled;
      // TODO: replace '_disabled' universally with 'clientreadonly'
      return _get(props.meta, 'property_meta._disabled', false) ||
        _get(props.meta, 'property_meta.clientreadonly', false) ||
        isMissingEditPermission.value;
    });

    const isRequired = computed(() => {
      if (props.actionParameter && props.actionParameter.required === 'Y') return true;
      if (!props.requiredFields) return false;

      const primaryKey = _get(props.meta, 'property_meta.primary_key');

      const required = props.requiredFields.includes(props.name);
      const isPersisted = props.isPersisted;
      const isPrimaryKey = primaryKey === true;

      const requiredOnNew = !isPersisted && required;
      const requiredOnEdit = isPersisted && required && !isPrimaryKey;

      return requiredOnNew || requiredOnEdit;
    });

    return {
      hasLockIcon,
      isAllowed,
      isPercent,
      isCurrency,
      isFieldAuth,
      isReadOnly,
      isMissingEditPermission,
      isDisabled,
      isRequired,
    };
  }

  function useRestricted() {
    const restrictedAttrName = computed(() => props.restrictableName || props.name);

    const restrictedLabels = computed(() => {
      if (!props.model.restricted_attributes) return [];
      return Object.entries(props.model.restricted_attributes).filter((attr) => {
        return attr[0] === restrictedAttrName.value || attr[0] === _get(props.meta, 'column_name');
      });
    });

    const restrictedLabel = computed(() => {
      if (!restrictedLabels.value.length) return null;
      return store.getters['translations/error'](
        restrictedLabels.value[0][1],
      );
    });

    const isRestricted = computed(() => {
      if (props.enabledOverride) return false;
      return restrictedLabels.value.length > 0;
    });

    const disableInput = computed(() => isDisabled.value || isReadOnly.value || isRestricted.value);

    /**
     * Displays a notify/growl if the input is a restricted attribute.
     */
    const displayRestrictedAttributeLabel = () => {
      if (!restrictedLabel.value) return;
      store.dispatch('notify/push', {
        title: 'Restricted Attribute',
        text: restrictedLabel.value,
        type: 'warning',
      });
    };

    return {
      isRestricted,
      disableInput,
      displayRestrictedAttributeLabel,
    };
  }

  function useFieldAuth() {
    const dialogActivator = ref(false);

    const mergedFieldAuthProps = computed(() => {
      return {
        name: props.name,
        model: props.model,
        parentPath: props.parentPath,
        isCurrency: isCurrency.value,
        classMeta: classSchema.value,
        ...props.fieldAuthProps,
      };
    });

    const updateFieldAuth = (payload) => {
      emit('update-field-auth', payload);
    };

    /**
     * Toggles the current state of the field authorization modal.
     */
    const toggleFieldAuth = () => {
      if (autoUpdateInFlight.value) {
        store.dispatch('notify/push', { title: 'Please wait...',
          text: 'Please try again momentarily...',
          type: 'error' });
        return;
      }

      dialogActivator.value = !dialogActivator.value;
      if (_get(props.fieldAuthProps, 'emitFieldAuth')) {
        // Only emit when told, otherwise handle internally in RInput
        emit('toggle-field-auth', { state: dialogActivator.value, key: props.name });
      }
    };

    return {
      dialogActivator,
      mergedFieldAuthProps,
      updateFieldAuth,
      toggleFieldAuth,
    };
  }

  function useTooltips() {
    const tooltipText = ref('');
    const showToolTip = ref(false);
    const tooltipHandler = ({ event, bool, text = null, rComponentName }) => {
      if (store.getters['editObject/showContextMenu']) return;
      if (!bool) {
        showToolTip.value = bool;
        return;
      }

      // RAutocomplete and RSelect both need to be handled differently do to using selection slot to display the selected value.
      const hasDropDown = ['RAutocomplete', 'RSelect', 'RCombobox'].includes(rComponentName);
      const isTextField = ['RTextField', 'RDatePicker'].includes(rComponentName);
      if (isTextField && disableInput.value) return;

      const buildArrText = (arr) => {
        let text = '';
        for (const [i, v] of arr.entries()) {
          if (!v._ui_label) continue;
          text += `${v._ui_label}${i === arr.length - 1 ? '' : '<br>'}`;
        }
        return text;
      };

      const key = hasDropDown ? 'selectionText' : props.name;
      const element = refs[key]?.value;
      if (element) {
        const offsetWidth = element?.offsetWidth;
        const scrollWidth = element?.scrollWidth;
        if (!propertyMeta.value?._multiple && scrollWidth <= offsetWidth) return;
        // Using localValue here only works since localValue is an array of objects when _multiple is true on RAutocomplete.
        // Using element.textContent only pulls the last item of the array.
        const multiSelectionText = Array.isArray(localValue.value) && buildArrText(localValue.value);
        tooltipText.value = multiSelectionText || text || element.textContent;
        showToolTip.value = (propertyMeta.value?._multiple || scrollWidth > offsetWidth) && bool && !masked.value;
        store.dispatch('editObject/toggleContextMenu', { show: false, event });
      }
    };

    return {
      tooltipText,
      showToolTip,
      tooltipHandler,
    };
  }

  function useManageValue() {
    const updateMessage = async ({ message, path, index }) => {
      const messageKey = `_${props.name}_errors`;
      if (!message) {
        await store.dispatch('editObject/updateValue', {
          key: messageKey,
          path,
          removeKey: true,
        });
        return;
      }

      emit('update-value', {
        key: messageKey,
        value: [message],
        path,
        index,
      });
    };

    /**
     * Start of mechanism for updating state of a complex record object
     * with a new value from input. Any appropriate santization should
     * be performed here (e.g. special characters, NaN checks if currency, etc).
     * @param {String} event - Value changed within input field.
     */
    const updateValue = (event, message = null) => {
      if (props.emitValueOnly) {
        emit('update-value', { key: props.name, path: props.parentPath, value: event, userInitiated: true });
        return;
      }
      // Loft UI #253 NOTE: v-text-field does not emit a change event when the value is changed to "the same" value.
      // This creates a (harmless) edge case bug where changing a corrected currency value
      // back to its incorrect value causes the incorrect currency value to be displayed
      // (due to this method not being called on said change event).
      // The corrected/formatted value is still retained retained correctly in editObject.record.
      // In situations where detail indices can change, we look to the store record
      // for the proper index, matching by _id
      let parentPath;
      let index;

      if (props.model._id && props.parentPath) {
        const { index: idx, parentPath: parPath } = getRelativePath(props.model, props.parentPath);
        index = idx;
        parentPath = parPath;
      }

      // any RInput derived fields should clear errors on data change
      // so updateMessage gets fired off always, if no message provided then it clears
      if (message || props.model[`_${props.name}_errors`]) {
        updateMessage({
          message,
          path: parentPath || props.parentPath,
          index: index !== -1 ? index : undefined,
        });

        if (message) return;
      }

      emit('update-value', {
        key: props.name,
        value: event,
        path: parentPath || props.parentPath,
        index: index !== -1 ? index : undefined,
        userInitiated: true,
      });
    };

    const localValue = computed(() => {
      // Use passed prop value, otherwpropsise use corresponding model property.
      const hasValueProp = !!(props.value === '' || isPresent(props.value));
      const value = hasValueProp ? props.value : props.model[props.name];

      return value;
    });

    const emitFieldEvent = (type, event) => {
      emit(type, event);
    };

    const handleSearchSubmit = (event) => {
      const form = event.target.form;
      if (form) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
      }
    };

    const handleEnterKey = async (event, cb = null) => {
      if (props.isSearchField) {
        if (cb) await cb();
        handleSearchSubmit(event);
        return;
      }

      emitFieldEvent('enter', event);
    };

    const setDefaultValue = () => {
      if (!isPresent(_get(props.actionParameter, 'defaultvalue')) || isPresent(localValue.value)) return;
      _set(props.model, props.name, props.actionParameter.defaultvalue);
    };


    return {
      updateValue,
      localValue,
      emitFieldEvent,
      handleEnterKey,
      handleSearchSubmit,
      setDefaultValue,
    };
  }

  return {
    classSchema,
    dialogActivator,
    displayRestrictedAttributeLabel,
    emitFieldEvent,
    localErrors,
    errorsAndWarningMessages,
    handleEnterKey,
    handleSearchSubmit,
    hasErrorMessages,
    hasLockIcon,
    hasWarnings,
    isAllowed,
    isCurrency,
    isDisabled,
    isFieldAuth,
    isMissingEditPermission,
    isPercent,
    isReadOnly,
    isRestricted,
    disableInput,
    isRequired,
    localLabel,
    localValue,
    masked,
    maskedRE,
    mergedFieldAuthProps,
    propertyMeta,
    setDefaultValue,
    showToolTip,
    toggleFieldAuth,
    tooltipHandler,
    tooltipText,
    updateFieldAuth,
    updateValue,
  };
};

export {
  baseRInputProps,
  baseRInputEmits,
  useBaseRInput,
};
